import { AriApiQueryCreatorFetch } from './AriApiCreator';
import { prismScopes } from './auth';
import getPrismBaseUrl, { prismFunctionCode } from './AIMApi/controllers/utils/getBaseUrl';

// Ungrouped response (when grouped=false or not specified)
/**
 * Represents the metadata for an individual media file, such as a property image, including its identification, grouping, and categorization details.
 *
 * @param id - The numeric identifier for the media file.
 * @param groupId - The unique string identifier for the group this file belongs to.
 * @param type - The general category of the media (e.g., 'Image').
 * @param subType - The specific subtype of the media (e.g., 'General', 'Thumbnail').
 * @param docName - The display name of the document or file.
 * @returns An object containing metadata for a media file stored in the system.
 */
export interface FileMetadata {
  id: number;
  groupId: string;
  type: string;
  subType: string;
  docName: string;
  thumbnailImage?: string;
  // Add other properties from your FileMetadata entity
}

// Grouped response (when grouped=true)
/**
 * Defines the structure for a grouped response of property images, categorizing them by sub-type with an associated count.
 *
 * @param type - The general category of the image (e.g., 'Image').
 * @param count - The number of images in this group.
 * @param subTypes - A list of subgroups grouped by sub-type.
 * @returns An object representing the count and grouping of property images by sub-type.
 */
export interface GroupedPropertyImagesResponse {
  type: string;
  count: number;
  subTypes: SubTypeGroup[];
}

/**
 * Represents a grouping of property images filtered and organized by their specific sub-type.
 *
 * @param count - The number of items in this specific subgroup.
 * @param subType - The sub-category type (e.g., 'General', 'Thumbnail').
 * @param images - An array of `FileMetadata` objects belonging to this group.
 * @returns An object representing a grouped collection of media files categorized by their sub-type.
 */
export interface SubTypeGroup {
  count: number;
  subType: string;
  images: FileMetadata[];
}

// Usage
/**
 * Represents the response type from the `Storage/GetPropertyImages/{propertyId}` endpoint — either an array of `FileMetadata` (ungrouped) or an array of `GroupedPropertyImagesResponse` entries (grouped by sub-type with counts).
 */
export type GetPropertyImagesResponse = FileMetadata[] | GroupedPropertyImagesResponse[];

// Upload request params
/**
 * Defines the parameters required to upload a new media file associated with a specific property record.
 *
 * @param propertyId - The unique identifier for the property record.
 * @param file - The binary file to be uploaded.
 * @param type - Optional category of the image (e.g., 'Image'). Defaults to 'Image'.
 * @param subType - Optional sub-category of the image (e.g., 'General'). Defaults to 'General'.
 * @param notes - Optional notes associated with the upload.
 * @param createdBy - The identifier for the user who created the upload.
 * @returns A `FileMetadata` object representing the successfully uploaded image.
 */
export interface UploadPropertyImageParams {
  propertyId: string;
  file: File;
  type?: string;
  subType?: string;
  notes?: string;
  createdBy?: string;
}

// Using the shared AriApi creator so auth token handling & function code injection are consistent.
/**
 * Provides API endpoints for managing property media, including fetching images (individual and grouped) and uploading new images associated with a specific property.
 *
 * @param propertyId - The unique identifier for the property to fetch images from.
 * @param grouped - Whether to group the results by sub-type. Defaults to false if not provided.
 * @returns An array of `FileMetadata` or a list of `GroupedPropertyImagesResponse`.
 */
export const mediaService = AriApiQueryCreatorFetch({
  serviceURL: getPrismBaseUrl(),
  scopesObj: prismScopes,
  functionCode: prismFunctionCode,
  apiOptions: {
    reducerPath: 'mediaApi',
    tagTypes: ['PropertyMedia'],
    endpoints: (builder) => ({
      media: builder.query<GetPropertyImagesResponse, { propertyId: string; grouped: boolean }>({
        query: ({ propertyId, grouped }) => ({
          url: `Storage/GetPropertyImages/${propertyId}`,
          // function code will be injected automatically via baseQuery; grouped passed explicitly
          params: { grouped }
        }),
        providesTags: (result, error, { propertyId }) => [{ type: 'PropertyMedia', id: propertyId }]
      }),
      fullImage: builder.query<string, { propertyId: string; id: string }>({
        query: ({ propertyId, id }) => ({
          url: `Storage/GetPropertyImage/${propertyId}/${id}/full`,
          responseHandler: 'text'
        })
      }),
      uploadPropertyImage: builder.mutation<FileMetadata, UploadPropertyImageParams>({
        query: ({ propertyId, file, type = 'Image', subType = 'General', notes, createdBy }) => {
          const formData = new FormData();
          formData.append('file', file, file.name);
          formData.append('type', type);
          formData.append('subType', subType);
          if (notes) formData.append('notes', notes);
          if (createdBy) formData.append('createdBy', createdBy);

          return {
            url: `Storage/UploadPropertyImage/${propertyId}`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: (result, error, { propertyId }) => [{ type: 'PropertyMedia', id: propertyId }]
      })
    })
  }
});

export const {
  useLazyMediaQuery,
  useLazyFullImageQuery,
  useMediaQuery,
  useUploadPropertyImageMutation
} = mediaService.hooks;
